<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { type CharacterActionLog, ImpactTypeEnum, OutcomeEnum } from '@/api/dx-backend';
import diceFace from '@/assets/icons/dice.png';
import { fallbackOnError } from '@/utils/imageFallback';
import { avatarPlaceholderUrl } from '@/utils/avatarPlaceholder';
import {
  actionArtOf,
  actorInfo,
  actorName,
  actorRelation,
  involvesCharacter,
  signedText,
  toneOfAction,
  toneOfImpact,
  violationIconOf
} from './actionLogShared';

const props = withDefaults(defineProps<{
  action: CharacterActionLog,
  selfCharacterId?: string | null
}>(), {
  selfCharacterId: null
});

const emit = defineEmits<{
  'character-hover': [payload: { charId: string; event: MouseEvent }];
  'character-hover-leave': [];
  'character-selected': [charId: string];
}>();

const { t } = useI18n();

const BADGE_TONE: Record<string, string> = {
  [OutcomeEnum.CriticalSuccess]: 'good',
  [OutcomeEnum.GoodLuck]: 'good',
  [OutcomeEnum.BadLuck]: 'bad',
  [OutcomeEnum.CriticalFail]: 'bad'
};

const enumKey = (value: unknown): string =>
  String(value ?? '').trim().toUpperCase().replace(/[\s-]+/g, '_');

const isAbsent = (value: unknown): boolean => {
  const key = enumKey(value);
  return !key || key === 'NONE' || key === 'NULL' || key === 'UNDEFINED';
};

const translateEnum = (prefix: string, value: unknown): string => {
  if (isAbsent(value)) return '';
  const key = `${prefix}.${enumKey(value)}`;
  const translated = t(key);
  return translated === key ? enumKey(value).replace(/_/g, ' ').toLowerCase() : translated;
};

const OUTCOME_SLUGS: Record<string, string> = {
  [OutcomeEnum.CriticalSuccess]: 'criticalSuccess',
  [OutcomeEnum.GoodLuck]: 'goodLuck',
  [OutcomeEnum.BaseValue]: 'baseValue',
  [OutcomeEnum.BadLuck]: 'badLuck',
  [OutcomeEnum.CriticalFail]: 'criticalFail'
};

const IMPACT_SLUGS: Record<string, string> = {
  [ImpactTypeEnum.Damage]: 'damage',
  [ImpactTypeEnum.Heal]: 'heal',
  [ImpactTypeEnum.Shield]: 'shield'
};

const VARIANTS: Record<string, Record<string, Record<string, number>>> = {
  criticalSuccess: { damage: { withViolation: 3, plain: 3 }, heal: { withViolation: 2, plain: 2 }, shield: { withViolation: 1, plain: 1 } },
  goodLuck: { damage: { withViolation: 2, plain: 2 }, heal: { withViolation: 1, plain: 1 }, shield: { withViolation: 1, plain: 1 } },
  baseValue: { damage: { withViolation: 3, plain: 3 }, heal: { withViolation: 2, plain: 2 }, shield: { withViolation: 1, plain: 1 } },
  badLuck: { damage: { withViolation: 3, plain: 3 }, heal: { withViolation: 2, plain: 2 }, shield: { withViolation: 1, plain: 1 } },
  criticalFail: { damage: { withViolation: 3, plain: 3 }, heal: { withViolation: 2, plain: 2 }, shield: { withViolation: 1, plain: 1 } }
};

const DEFAULT_VARIANTS: Record<string, number> = { full: 1, noViolation: 1, noType: 1, plain: 1 };

/* Pick the phrasing from the impact id rather than at random, so a re-render
   never rewrites a line the player has already read. */
const variantIndex = (seed: string, count: number): number => {
  if (count <= 1) return 0;
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) >>> 0;
  }
  return hash % count;
};

const describeImpact = (impact: any, type: string, violation: string, size: number): string => {
  const outcome = impact.dice_roll_result?.outcome;
  const data = {
    size,
    formattedSize: size,
    formattedSizeWithPrep: size,
    type,
    violation,
    skill: (props.action.skill as any)?.name || t('actionLog.unknownSkill'),
    item: t('actionLog.unknownItem'),
    outcome: translateEnum('actionLog.outcomes', outcome) || t('actionLog.outcomes.unknown')
  };

  const outcomeSlug = OUTCOME_SLUGS[outcome];
  if (!outcomeSlug) return t('actionLog.outcomes.genericOutcome', data);

  const groupSlug = IMPACT_SLUGS[impact.type];
  const variants = groupSlug ? VARIANTS[outcomeSlug]?.[groupSlug] : DEFAULT_VARIANTS;
  if (!variants) return t('actionLog.outcomes.genericOutcome', data);

  let variant: string;
  if (!groupSlug) {
    if (type && violation) variant = 'full';
    else if (type) variant = 'noViolation';
    else if (violation) variant = 'noType';
    else variant = 'plain';
  } else {
    variant = violation ? 'withViolation' : 'plain';
  }

  const count = variants[variant] || 1;
  const key = `actionLog.outcomes.${outcomeSlug}.${groupSlug || 'default'}.${variant}.${variantIndex(String(impact.id || ''), count) + 1}`;
  const text = t(key, data);
  return text === key ? t('actionLog.outcomes.genericOutcome', data) : text;
};

const art = computed(() => actionArtOf(props.action));
const tone = computed(() => toneOfAction(props.action));
const isSelf = computed(() => involvesCharacter(props.action, props.selfCharacterId));

const targetId = computed<string | null>(() =>
  (props.action.targets || []).find((id) => id !== props.action.initiator) || null
);

const verb = computed(() => {
  const type = enumKey(props.action.action_type);
  const done = props.action.accepted !== false && props.action.performed !== false;
  const key = `${done ? 'actionTypeEnumPerformed' : 'actionTypeEnumAttempted'}.${type}`;
  const translated = t(key);
  return translated === key ? translateEnum('actionLog.actionTypes', props.action.action_type) : translated;
});

const skillName = computed<string>(() => (props.action.skill as any)?.name || '');

const selfTargeted = computed(() =>
  !targetId.value && (props.action.targets || []).includes(props.action.initiator)
);

const status = computed(() => {
  if (props.action.accepted === false) return { key: 'actionLog.status.notAccepted', tone: 'bad' };
  if (props.action.performed === false) return { key: 'actionLog.status.notPerformed', tone: 'idle' };
  return null;
});

const nameOf = (charId: string | null | undefined): string => {
  if (charId && charId === props.selfCharacterId) return t('actionLog.you');
  return actorName(charId) || t('actionLog.unknownCharacter');
};

/*
 * The roster the log reads actors from (`actionLogShared.ActorInfo`) carries an
 * id, a name, an avatar and a relation — no gender — so both the missing-avatar
 * case and the broken-file case land on `other.png`, the default the server's
 * own `CharacterBiography.avatar` uses.
 */
const avatarFallback = avatarPlaceholderUrl();

const avatarOf = (charId: string | null | undefined): string =>
  actorInfo(charId)?.avatar || avatarFallback;

const onAvatarError = (event: Event) => fallbackOnError(event, avatarFallback);

const impacts = computed(() =>
  (props.action.impacts || []).map((impact: any) => {
    const impactTone = toneOfImpact(impact);
    const size = Math.abs(impact.size || 0);
    const outcome = impact.dice_roll_result?.outcome;
    const type = translateEnum('actionLog.impactTypes', impact.type);
    const violation = translateEnum('actionLog.violationTypes', impact.violation);
    return {
      id: impact.id,
      tone: impactTone,
      value: size ? signedText(size, impactTone) : '—',
      type,
      violation,
      story: describeImpact(impact, type, violation, size),
      icon: violationIconOf(impact.violation),
      dice: impact.dice_roll_result?.dice_side ?? null,
      outcome: BADGE_TONE[outcome] ? translateEnum('actionLog.outcomes', outcome) : '',
      badgeTone: BADGE_TONE[outcome] || '',
      target: impact.target && impact.target !== props.action.initiator ? impact.target : null
    };
  })
);

const onHover = (charId: string, event: MouseEvent) => emit('character-hover', { charId, event });
</script>

<template>
  <article class="card" :class="[`tone-${tone}`, { 'is-self': isSelf }]">
    <div class="band">
      <img
          class="cast-art is-left"
          :src="avatarOf(action.initiator)"
          :alt="nameOf(action.initiator)"
          loading="lazy"
          @error="onAvatarError"
          @mouseover="onHover(action.initiator, $event)"
          @mouseleave="emit('character-hover-leave')"
          @click="emit('character-selected', action.initiator)"
      />
      <img
          v-if="targetId"
          class="cast-art is-right"
          :src="avatarOf(targetId)"
          :alt="nameOf(targetId)"
          loading="lazy"
          @error="onAvatarError"
          @mouseover="onHover(targetId, $event)"
          @mouseleave="emit('character-hover-leave')"
          @click="emit('character-selected', targetId)"
      />
      <img v-if="art" class="band-art" :src="art" alt="" aria-hidden="true" loading="lazy" />
      <span class="band-scrim" aria-hidden="true"></span>

      <button
          type="button"
          class="cast-name is-left"
          :class="`relation-${actorRelation(action.initiator)}`"
          @mouseover="onHover(action.initiator, $event)"
          @mouseleave="emit('character-hover-leave')"
          @click="emit('character-selected', action.initiator)"
      >{{ nameOf(action.initiator) }}</button>

      <p class="band-center">
        <span class="band-verb">{{ verb }}</span>
        <span v-if="skillName" class="band-headline">{{ skillName }}</span>
      </p>

      <button
          v-if="targetId"
          type="button"
          class="cast-name is-right"
          :class="`relation-${actorRelation(targetId)}`"
          @mouseover="onHover(targetId, $event)"
          @mouseleave="emit('character-hover-leave')"
          @click="emit('character-selected', targetId)"
      >{{ nameOf(targetId) }}</button>
      <span v-else class="cast-name is-right is-quiet">{{ selfTargeted ? t('actionLog.self') : '' }}</span>

      <span v-if="status" class="band-status" :class="`is-${status.tone}`">{{ t(status.key) }}</span>
    </div>

    <div v-for="impact in impacts" :key="impact.id" class="strike" :class="`tone-${impact.tone}`">
      <img v-if="impact.icon" class="strike-art" :src="impact.icon" alt="" aria-hidden="true" loading="lazy" />

      <img v-if="impact.icon" class="strike-icon" :src="impact.icon" :alt="impact.violation" loading="lazy" />

      <span class="strike-value">{{ impact.value }}</span>

      <span class="strike-type">{{ impact.type }}</span>

      <button
          v-if="impact.target && impact.target !== targetId"
          type="button"
          class="strike-target"
          @mouseover="onHover(impact.target, $event)"
          @mouseleave="emit('character-hover-leave')"
          @click="emit('character-selected', impact.target)"
      >{{ nameOf(impact.target) }}</button>

      <span class="strike-story" :title="impact.story">{{ impact.story }}</span>

      <span v-if="impact.outcome" class="strike-outcome" :class="`is-${impact.badgeTone}`">{{ impact.outcome }}</span>

      <span v-if="impact.dice !== null" class="strike-dice" :title="t('actionLog.diceRoll', { value: impact.dice })">
        <img :src="diceFace" alt="" aria-hidden="true" />
        <span>{{ impact.dice }}</span>
      </span>
    </div>
  </article>
</template>

<style scoped>
.card {
  margin-bottom: 0.3rem;
  border: 1px solid rgba(250, 218, 149, 0.14);
  border-left: 3px solid rgba(232, 238, 245, 0.14);
  border-radius: 4px;
  background: rgba(18, 20, 26, 0.9);
  overflow: hidden;
}

.card.tone-damage {
  border-left-color: var(--lg-dmg);
}

.card.tone-heal {
  border-left-color: var(--lg-heal);
}

.card.tone-buff {
  border-left-color: var(--lg-buff);
}

.card.tone-debuff {
  border-left-color: var(--lg-debuff);
}

.card.is-self {
  box-shadow: inset 0 0 0 1px rgba(74, 217, 255, 0.22);
}

.band {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.5fr) minmax(0, 1fr);
  align-items: center;
  min-height: 52px;
  overflow: hidden;
}

.cast-art {
  position: absolute;
  top: 0;
  width: 30%;
  height: 100%;
  object-fit: cover;
  object-position: center 14%;
  filter: brightness(1.12) contrast(1.05);
  cursor: pointer;
}

.cast-art.is-left {
  left: 0;
  mask: linear-gradient(to right, rgba(0, 0, 0, 1) 25%, rgba(0, 0, 0, 0) 92%);
  -webkit-mask: linear-gradient(to right, rgba(0, 0, 0, 1) 25%, rgba(0, 0, 0, 0) 92%);
}

.cast-art.is-right {
  right: 0;
  mask: linear-gradient(to left, rgba(0, 0, 0, 1) 25%, rgba(0, 0, 0, 0) 92%);
  -webkit-mask: linear-gradient(to left, rgba(0, 0, 0, 1) 25%, rgba(0, 0, 0, 0) 92%);
}

.band-art {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 46%;
  height: 130%;
  transform: translate(-50%, -50%);
  object-fit: cover;
  opacity: 0.45;
  mask: radial-gradient(circle at center, rgba(0, 0, 0, 1) 25%, rgba(0, 0, 0, 0) 72%);
  -webkit-mask: radial-gradient(circle at center, rgba(0, 0, 0, 1) 25%, rgba(0, 0, 0, 0) 72%);
  pointer-events: none;
}

.band-scrim {
  position: absolute;
  inset: 0;
  background: linear-gradient(
      90deg,
      rgba(12, 14, 18, 0.4) 0%,
      rgba(12, 14, 18, 0.55) 28%,
      rgba(12, 14, 18, 0.62) 50%,
      rgba(12, 14, 18, 0.55) 72%,
      rgba(12, 14, 18, 0.4) 100%
  );
  pointer-events: none;
}

.cast-name {
  position: relative;
  z-index: 1;
  align-self: end;
  padding: 0 0.6rem 0.35rem;
  border: 0;
  background: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: 'Cinzel', 'Times New Roman', Georgia, serif;
  font-size: 0.82rem;
  font-weight: 700;
  text-shadow: 0 1px 3px #000, 0 0 10px rgba(0, 0, 0, 0.9);
  color: #d8dee9;
  cursor: pointer;
}

.cast-name.is-quiet {
  font-family: 'Arial Narrow', 'Roboto Condensed', system-ui, sans-serif;
  font-size: 0.72rem;
  font-weight: 400;
  color: rgba(232, 238, 245, 0.45);
  cursor: default;
}

.cast-name.is-left,
.cast-name.is-right {
  text-align: center;
}

.cast-name.relation-self {
  color: var(--lg-gold);
}

.cast-name.relation-ally {
  color: #9ff0be;
}

.cast-name.relation-enemy {
  color: #ff9aa4;
}

.cast-name:hover,
.cast-name:focus-visible {
  color: #fff;
  outline: none;
}

.band-center {
  position: relative;
  z-index: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: center;
  gap: 0.35rem;
  margin: 0;
  padding: 0 0.4rem;
  text-align: center;
}

.band-verb {
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.54rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(232, 238, 245, 0.6);
  text-shadow: 0 1px 3px #000;
}

.band-headline {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.86rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--lg-gold);
  text-shadow: 0 1px 4px #000, 0 0 14px rgba(0, 0, 0, 0.8);
}

.band-status {
  position: absolute;
  top: 0.3rem;
  right: 0.4rem;
  z-index: 2;
  padding: 0.05rem 0.35rem;
  border-radius: 2px;
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.5rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.band-status.is-bad {
  color: #16130c;
  background: var(--lg-dmg);
}

.band-status.is-idle {
  color: rgba(232, 238, 245, 0.7);
  background: rgba(0, 0, 0, 0.6);
  box-shadow: inset 0 0 0 1px rgba(232, 238, 245, 0.18);
}

.strike {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.15rem 0.5rem;
  border-top: 1px solid rgba(250, 218, 149, 0.12);
  background: rgba(0, 0, 0, 0.28);
  overflow: hidden;
}

.strike-art {
  position: absolute;
  top: 0;
  right: 0;
  width: 60%;
  height: 100%;
  object-fit: cover;
  opacity: 0.35;
  mask: radial-gradient(circle at 60% center, rgba(0, 0, 0, 0.5) 25%, rgba(0, 0, 0, 0) 85%);
  -webkit-mask: radial-gradient(circle at 60% center, rgba(0, 0, 0, 0.5) 25%, rgba(0, 0, 0, 0) 85%);
  pointer-events: none;
}

.strike-value {
  position: relative;
  min-width: 2.4rem;
  text-align: right;
  font-family: 'Orbitron', ui-monospace, system-ui, sans-serif;
  font-size: 1.05rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
  color: var(--lg-gold);
  text-shadow: 0 1px 3px #000;
}

.strike.tone-damage .strike-value {
  color: var(--lg-dmg);
}

.strike.tone-heal .strike-value {
  color: var(--lg-heal);
}

.strike.tone-buff .strike-value {
  color: var(--lg-buff);
}

.strike.tone-debuff .strike-value {
  color: var(--lg-debuff);
}

.strike-type {
  position: relative;
  flex: 0 0 auto;
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.56rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  white-space: nowrap;
  color: var(--lg-ink);
}

.strike-story {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: 'Arial Narrow', 'Roboto Condensed', system-ui, sans-serif;
  font-size: 0.76rem;
  line-height: 1.3;
  color: rgba(232, 238, 245, 0.52);
  text-shadow: 0 1px 3px #000;
}

.strike-target {
  position: relative;
  flex: 0 0 auto;
  padding: 0;
  border: 0;
  background: none;
  font-family: 'Cinzel', 'Times New Roman', Georgia, serif;
  font-size: 0.76rem;
  font-weight: 700;
  color: var(--lg-gold);
  cursor: pointer;
}

.strike-outcome {
  position: relative;
  padding: 0.05rem 0.35rem;
  border-radius: 2px;
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.52rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  white-space: nowrap;
  color: rgba(232, 238, 245, 0.75);
  box-shadow: inset 0 0 0 1px rgba(232, 238, 245, 0.16);
}

.strike-outcome.is-good {
  color: #16130c;
  background: var(--lg-buff);
  box-shadow: none;
}

.strike-outcome.is-bad {
  color: var(--lg-ink);
  background: rgba(255, 77, 94, 0.3);
  box-shadow: inset 0 0 0 1px rgba(255, 77, 94, 0.6);
}

.strike-dice {
  position: relative;
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
}

.strike-dice img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  opacity: 0.9;
}

.strike-dice span {
  position: relative;
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.74rem;
  font-weight: 700;
  color: #fff;
  text-shadow: 0 1px 3px #000, 0 0 6px #000;
}

.strike-icon {
  position: relative;
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  object-fit: cover;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 3px;
}

.strike-outcome {
  flex: 0 0 auto;
}

@media (max-width: 640px) {
  .band {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr) minmax(0, 1fr);
    min-height: 42px;
  }

  .cast-name {
    font-size: 0.74rem;
    padding: 0 0.35rem;
  }

  .band-headline {
    font-size: 0.78rem;
  }

  .strike-story {
    display: none;
  }
}
</style>
